import { Badge, Button, colors, Stack, Typography } from '@ui-components/neo.tax';
import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';

import {
  CONNECTION_METHOD_NAMES,
  CONNECTION_STATUS_LABELS,
  formatDateTime,
  TicketingConnectionAction,
  TicketingConnectionRow,
  TicketingConnectionStatus,
} from '../../../utils';

/** Colors distinguish healthy, attention-needed, and interrupted connection states. */
const STATUS_COLORS: Record<TicketingConnectionStatus, 'blue' | 'red' | 'yellow' | 'default'> = {
  [TicketingConnectionStatus.Connected]: 'blue',
  [TicketingConnectionStatus.SupersededByGitHubApp]: 'default',
  [TicketingConnectionStatus.NeedsRelink]: 'yellow',
  [TicketingConnectionStatus.WaitingForInstall]: 'default',
  [TicketingConnectionStatus.SuspendedInGitHub]: 'red',
  [TicketingConnectionStatus.UninstalledFromGitHub]: 'red',
};

/**
 * A status the customer cannot act on from the row itself needs a sentence saying where the fix
 * lives — otherwise a red badge states a problem and offers nothing.
 */
const STATUS_HINTS: Partial<Record<TicketingConnectionStatus, string>> = {
  [TicketingConnectionStatus.SuspendedInGitHub]: 'Unsuspend the app in GitHub to restore it.',
  [TicketingConnectionStatus.NeedsRelink]: 'Reconnect this provider to replace the credential.',
  [TicketingConnectionStatus.WaitingForInstall]: 'Finish installing the Neo.Tax app in GitHub.',
  [TicketingConnectionStatus.UninstalledFromGitHub]: 'Install the Neo.Tax app again to reconnect.',
};

const ACTION_LABELS: Record<TicketingConnectionAction, string> = {
  [TicketingConnectionAction.MigrateToGitHubApp]: 'Migrate to GitHub App',
  [TicketingConnectionAction.Relink]: 'Relink',
  [TicketingConnectionAction.ResumeInstall]: 'Resume install',
  [TicketingConnectionAction.Reconnect]: 'Reconnect',
  [TicketingConnectionAction.Disconnect]: 'Disconnect',
};

const NOT_SYNCED_YET = 'Never';

/** Most of the row is one value read straight off it, rendered the same way. */
const textColumn = <Field extends keyof TicketingConnectionRow>({
  accessor,
  header,
  read,
  width,
}: {
  accessor: Field;
  header: string;
  width: number;
  read?: (value: TicketingConnectionRow[Field]) => string;
}): Column<TicketingConnectionRow> =>
  // react-table's Column ties Cell's value type to a literal accessor, so it cannot express a
  // factory generic over which field is read; the generic above is what actually checks `read`.
  ({
    accessor,
    width,
    Header: header,
    Cell: ({
      value,
    }: React.PropsWithChildren<CellProps<TicketingConnectionRow, TicketingConnectionRow[Field]>>): JSX.Element => (
      <Typography fontSize="xs" color={colors.gray[900]}>
        {read ? read(value) : String(value)}
      </Typography>
    ),
  }) as Column<TicketingConnectionRow>;

export const useTicketingConnectionColumns = ({
  onAction,
  supportedActions,
}: {
  /** The actions this surface can actually perform; anything else is not offered. */
  supportedActions: ReadonlySet<TicketingConnectionAction>;
  onAction: (action: TicketingConnectionAction, connection: TicketingConnectionRow) => void;
}): Column<TicketingConnectionRow>[] =>
  useMemo(
    () => [
      textColumn({ accessor: 'provider', header: 'Provider', width: 160 }),
      textColumn({ accessor: 'method', header: 'Connected with', width: 220 }),
      {
        id: 'status',
        width: 200,
        Header: 'Status',
        accessor: (datum: TicketingConnectionRow): TicketingConnectionRow => datum,
        Cell: ({
          value,
        }: React.PropsWithChildren<CellProps<TicketingConnectionRow, TicketingConnectionRow>>): JSX.Element => (
          <Stack direction="column" alignItems="start" gap={1}>
            <Badge className="whitespace-nowrap" dot={false} colorVariant={STATUS_COLORS[value.status]}>
              {CONNECTION_STATUS_LABELS[value.status]}
            </Badge>
            {STATUS_HINTS[value.status] ? (
              <Typography fontSize="xs" color={colors.gray[600]}>
                {STATUS_HINTS[value.status]}
              </Typography>
            ) : (
              <></>
            )}
          </Stack>
        ),
      },
      textColumn({ accessor: 'connectedAt', header: 'Connected on', width: 160, read: formatDateTime }),
      {
        id: 'lastSuccessfulSync',
        width: 220,
        Header: 'Last synced',
        accessor: (datum: TicketingConnectionRow): TicketingConnectionRow['lastSuccessfulSync'] =>
          datum.lastSuccessfulSync,
        // The method is shown as well as the time, because during a migration a company holds both a
        // Merge and a GitHub App connection and the time alone does not say which one brought the
        // data in. It is the method that sync actually ran through, which for a superseded row is
        // not necessarily the method the row is connected with today.
        Cell: ({
          value,
        }: React.PropsWithChildren<
          CellProps<TicketingConnectionRow, TicketingConnectionRow['lastSuccessfulSync']>
        >): JSX.Element =>
          value ? (
            <Stack direction="column" gap={1}>
              <Typography fontSize="xs" color={colors.gray[900]}>
                {formatDateTime(value.at)}
              </Typography>
              <Typography fontSize="xs" color={colors.gray[600]}>
                {CONNECTION_METHOD_NAMES[value.acquisitionMethod]}
              </Typography>
            </Stack>
          ) : (
            <Typography fontSize="xs" color={colors.gray[900]}>
              {NOT_SYNCED_YET}
            </Typography>
          ),
      },
      {
        id: 'actions',
        width: 'auto',
        accessor: (datum: TicketingConnectionRow): TicketingConnectionRow => datum,
        Cell: ({
          value,
        }: React.PropsWithChildren<CellProps<TicketingConnectionRow, TicketingConnectionRow>>): JSX.Element => (
          <Stack direction="row" justifyContent="end" gap={2} fullWidth>
            {value.actions
              .filter((action) => supportedActions.has(action))
              .map((action) => (
                <Button
                  key={action}
                  onClick={(): void => onAction(action, value)}
                  size="xs"
                  variant="action"
                  rounded="regular"
                >
                  {ACTION_LABELS[action]}
                </Button>
              ))}
          </Stack>
        ),
      },
    ],
    [onAction, supportedActions],
  );
